/**
 * Institution-form downloads, single and bulk.
 *
 * **An institution form is rendered on demand, so the download can be refused.**
 * The backend fills the template against the matter at request time and refuses
 * outright where filling it would produce a document that lies — today that is
 * `DESTINATION_ACCOUNT_NOT_SET`, where a form tells an institution which account
 * to pay into and the firm has not set one, so the boxes would print blank on a
 * paper an executor signs and a broker acts on.
 *
 * The refusal carries a sentence written for the lawyer, naming the form and
 * the account to add. Every call site here fetches and reads it, because the
 * three that used `window.open(url)` could not: the action they called only
 * built a URL and never touched the backend, so it reported success, the caller
 * toasted "Downloading 2 forms", and the refusal arrived in a new browser tab as
 * raw JSON. That is the LF-6106 defect on the declaration download, in a second
 * place (VALE-1513).
 *
 * Both functions resolve rather than throw: a refusal is an instruction to the
 * lawyer, not an exception, and every caller renders it the same way.
 */

import {
  filenameFromContentDisposition,
  readRefusalMessage,
  triggerBlobDownload
} from '@/lib/utils/browser-download';

/** Resolved outcome of a download attempt. `error` is what the user should read. */
export interface FormDownloadResult {
  success: boolean;
  error?: string;
}

export interface FormDownloadOptions {
  signatoryContactId?: number;
  inventoryItemId?: number;
}

/** One form in a bulk selection. */
export interface BulkFormDownloadItem {
  institutionId: number;
  formId: number;
  signatoryContactId?: number;
  inventoryItemId?: number;
}

/*
  Binary content goes through the proxy rather than `directApiClient` — that is
  the standing policy for a streamed body (`app/api/proxy/CLAUDE.md`). The proxy
  forwards a non-2xx JSON body and its status verbatim, which is what makes the
  refusal readable here.
*/
function formDownloadPath(
  matterId: number,
  institutionId: number,
  formId: number | string,
  options?: FormDownloadOptions
): string {
  const query = new URLSearchParams();
  if (options?.signatoryContactId) {
    query.append('signatoryContactId', String(options.signatoryContactId));
  }
  if (options?.inventoryItemId) {
    query.append('inventoryItemId', String(options.inventoryItemId));
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `/api/proxy/matters/${matterId}/institutions/${institutionId}/forms/${formId}/download${suffix}`;
}

/** Fetch one institution form and hand it to the browser, or return why not. */
export async function downloadInstitutionForm(
  matterId: number,
  institutionId: number,
  formId: number | string,
  options?: FormDownloadOptions
): Promise<FormDownloadResult> {
  try {
    const response = await fetch(
      formDownloadPath(matterId, institutionId, formId, options)
    );

    if (!response.ok) {
      return {
        success: false,
        error: await readRefusalMessage(response, 'Failed to download form')
      };
    }

    const blob = await response.blob();
    triggerBlobDownload(
      blob,
      filenameFromContentDisposition(
        response.headers.get('content-disposition')
      )
    );
    return { success: true };
  } catch {
    return { success: false, error: 'Failed to download form' };
  }
}

/**
 * Fetch a selection of institution forms as one zip.
 *
 * **A refused form fails the whole selection, deliberately.** The alternative
 * considered was zipping what succeeded and naming the rest, and it is the more
 * dangerous answer here: the pack a lawyer downloads is the pack they post, and
 * a zip quietly missing the sale authority is worse than a zip that never
 * arrived. The backend names every refused form in one message so the remedy is
 * still a single action.
 */
export async function downloadInstitutionFormsZip(
  matterId: number,
  forms: BulkFormDownloadItem[]
): Promise<FormDownloadResult> {
  try {
    const response = await fetch(
      `/api/proxy/matters/${matterId}/forms/download-zip`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forms })
      }
    );

    if (!response.ok) {
      return {
        success: false,
        error: await readRefusalMessage(response, 'Failed to download forms')
      };
    }

    const blob = await response.blob();
    triggerBlobDownload(
      blob,
      filenameFromContentDisposition(
        response.headers.get('content-disposition')
      )
    );
    return { success: true };
  } catch {
    return { success: false, error: 'Failed to download forms' };
  }
}
